import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Componente que garante que a página sempre role para o topo
 * quando há uma mudança de rota/navegação
 */
export function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathnameRef = useRef<string>(pathname);

  useEffect(() => {
    // Só executa se a rota realmente mudou
    if (prevPathnameRef.current === pathname) {
      return;
    }

    prevPathnameRef.current = pathname;

    // Desabilita a restauração automática de scroll do navegador
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Função para fazer scroll para o topo
    const scrollToTop = () => {
      // Tenta múltiplas formas para garantir compatibilidade
      window.scrollTo(0, 0);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Para elementos com scroll interno (como main, div com overflow)
      const scrollableSelectors = ['main', '[role="main"]', '.overflow-y-auto', '.overflow-y-scroll'];
      scrollableSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          if (element instanceof HTMLElement) {
            element.scrollTop = 0;
          }
        });
      });
    };

    // Scroll imediato
    scrollToTop();
    
    // Usa requestAnimationFrame para garantir que o scroll aconteça após o render
    requestAnimationFrame(() => {
      scrollToTop();
      // Duplo requestAnimationFrame para garantir que aconteça após qualquer layout
      requestAnimationFrame(() => {
        scrollToTop();
      });
    });

    // Fallback adicional: tenta novamente após delays progressivos
    // Isso garante que mesmo se houver algum componente que faça scroll após renderizar,
    // ainda vamos resetar para o topo
    const timeoutIds = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
      setTimeout(scrollToTop, 200),
      setTimeout(scrollToTop, 300),
      setTimeout(scrollToTop, 500), // Adiciona um timeout extra para garantir
    ];

    // Intercepta qualquer scroll que aconteça nos primeiros 800ms após a navegação
    let scrollBlocked = true;
    const handleScroll = (e?: Event) => {
      if (scrollBlocked) {
        e?.preventDefault();
        scrollToTop();
      }
    };

    // Adiciona listeners de scroll em window e elementos scrolláveis
    window.addEventListener('scroll', handleScroll, { passive: false });
    window.addEventListener('wheel', handleScroll, { passive: false });
    window.addEventListener('touchmove', handleScroll, { passive: false });
    
    // Remove o bloqueio após 800ms (aumentado de 500ms)
    const unblockTimeout = setTimeout(() => {
      scrollBlocked = false;
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
    }, 800);

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
      clearTimeout(unblockTimeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      scrollBlocked = false;
    };
  }, [pathname]);

  return null;
}

